import type React from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useLocation } from "react-router"

import { ErrorBoundaryContentFallback } from "@/components/error-boundary/error-boundary-content-fallback"
import { logError } from "@/components/error-boundary/error-boundary-fallback-utils"
import { PageHeader } from "@/components/nav/page-header"
import { Page } from "@/components/page"

import { Box, Stack } from "@mui/material"

interface AuthenticatedPageLayoutProps {
  children: React.ReactNode
  /** When true, applies Page full-height semantics to the content region. */
  fullHeight?: boolean
  /**
   * Optional secondary chrome (tabs or action bar). Renders outside the
   * content error boundary. Callers own sticky / hasTabs behavior.
   */
  secondary?: React.ReactNode
  /** When set, renders PageHeader. Omit under tab parents that already own the title. */
  title?: string
  /** Optional id for the page title heading (PageHeader `id`). */
  titleId?: string
  /**
   * When false, skips the Page wrapper (Outlet parents / Sites DataGuard).
   * @default true
   */
  wrapInPage?: boolean
}

/**
 * Authenticated page chrome with a content-scoped error boundary.
 * Header and secondary stay mounted when content throws.
 */
export const AuthenticatedPageLayout: React.FC<
  AuthenticatedPageLayoutProps
> = ({
  children,
  fullHeight = false,
  secondary,
  title,
  titleId,
  wrapInPage = true,
}) => {
  const location = useLocation()

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      {title != null && <PageHeader id={titleId} title={title} />}
      {secondary}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <ErrorBoundary
          FallbackComponent={ErrorBoundaryContentFallback}
          onError={logError}
          resetKeys={[location.pathname]}
        >
          {wrapInPage ? (
            <Page fullHeight={fullHeight}>{children}</Page>
          ) : (
            children
          )}
        </ErrorBoundary>
      </Box>
    </Stack>
  )
}
